import { SSEEvent } from "./types";

/**
 * Options for fetchSSEStream
 */
export interface FetchSSEStreamOptions {
  /** Bearer token for authorization */
  token: string;
  /** Request body to send */
  body: object;
  /** Optional AbortSignal for cancellation */
  signal?: AbortSignal;
  /**
   * Activity timeout in milliseconds (default: 180000 = 3 minutes).
   * The timeout resets each time data is received from the stream.
   * This prevents timing out during long-running but active streams.
   */
  timeout?: number;
}

/**
 * Error thrown when SSE stream encounters an issue
 */
export class SSEStreamError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly isTimeout?: boolean
  ) {
    super(message);
    this.name = "SSEStreamError";
  }
}

/**
 * Streams Server-Sent Events from a POST endpoint using fetch and ReadableStream.
 *
 * Unlike native EventSource (which only supports GET), this function uses fetch
 * to make POST requests and manually parses the SSE protocol.
 *
 * @param url - The endpoint URL
 * @param options - Configuration options including token, body, and optional signal
 * @yields SSEEvent tuples as they arrive from the server
 * @throws SSEStreamError on HTTP errors, network failures, or timeouts
 *
 * @example
 * ```typescript
 * const controller = new AbortController();
 *
 * try {
 *   for await (const event of fetchSSEStream(url, {
 *     token: 'bearer-token',
 *     body: { content: 'Hello' },
 *     signal: controller.signal,
 *   })) {
 *     if (event[0] === 'progress') {
 *       console.log('Progress:', event[1].progress_percent);
 *     }
 *   }
 * } catch (error) {
 *   if (error instanceof SSEStreamError) {
 *     console.error('Stream error:', error.message);
 *   }
 * }
 * ```
 */
export async function* fetchSSEStream(
  url: string,
  options: FetchSSEStreamOptions
): AsyncGenerator<SSEEvent, void, undefined> {
  const { token, body, signal, timeout = 180000 } = options;

  // Create timeout controller for activity-based timeout
  const timeoutController = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout>;
  let isTimedOut = false;

  // Function to reset the activity timeout
  const resetTimeout = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      isTimedOut = true;
      timeoutController.abort();
    }, timeout);
  };

  // Start the initial timeout
  resetTimeout();

  // Combine signals if one was provided
  const combinedSignal = signal
    ? combineAbortSignals(signal, timeoutController.signal)
    : timeoutController.signal;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      signal: combinedSignal,
    });

    // Reset timeout after successful connection
    resetTimeout();

    if (!response.ok) {
      throw new SSEStreamError(
        `HTTP error: ${response.status} ${response.statusText}`,
        response.status
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new SSEStreamError("Response body is not readable");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        // Reset timeout on each chunk received (activity-based timeout)
        resetTimeout();

        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            const events = parseSSEBuffer(buffer);
            for (const event of events) {
              yield event;
            }
          }
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // SSE messages are separated by double newlines
        const parts = buffer.split("\n\n");
        // Keep the last potentially incomplete part in the buffer
        buffer = parts.pop() || "";

        // Process complete messages
        for (const part of parts) {
          const event = parseSSEMessage(part);
          if (event) {
            yield event;
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    if (error instanceof SSEStreamError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.name === "AbortError") {
        // Check if it was a timeout or user cancellation
        if (isTimedOut && !signal?.aborted) {
          throw new SSEStreamError(
            `Stream timed out after ${timeout / 1000} seconds of inactivity`,
            undefined,
            true
          );
        }
        throw new SSEStreamError("Stream was cancelled");
      }
      throw new SSEStreamError(`Network error: ${error.message}`);
    }

    throw new SSEStreamError("Unknown error occurred");
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Parses a single SSE message from raw text
 */
function parseSSEMessage(message: string): SSEEvent | null {
  const lines = message.split("\n");

  for (const line of lines) {
    // SSE data lines start with "data: "
    if (line.startsWith("data: ")) {
      const jsonString = line.slice(6); // Remove "data: " prefix

      try {
        const parsed = JSON.parse(jsonString) as SSEEvent;
        return parsed;
      } catch (e) {
        console.error("Failed to parse SSE event:", jsonString, e);
        return null;
      }
    }
  }

  return null;
}

/**
 * Parses multiple SSE messages from a buffer (for final flush)
 */
function parseSSEBuffer(buffer: string): SSEEvent[] {
  const events: SSEEvent[] = [];
  const parts = buffer.split("\n\n");

  for (const part of parts) {
    const event = parseSSEMessage(part);
    if (event) {
      events.push(event);
    }
  }

  return events;
}

/**
 * Combines multiple AbortSignals into one
 */
function combineAbortSignals(...signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();

  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort();
      break;
    }
    signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  return controller.signal;
}

/**
 * Creates a cancellable stream wrapper with abort controller
 */
export function createCancellableStream(
  url: string,
  options: Omit<FetchSSEStreamOptions, "signal">
): {
  stream: AsyncGenerator<SSEEvent, void, undefined>;
  cancel: () => void;
} {
  const controller = new AbortController();

  return {
    stream: fetchSSEStream(url, { ...options, signal: controller.signal }),
    cancel: () => controller.abort(),
  };
}
